import { type ApiFetchContext, apiFetch } from "../client";
import type {
  AvailableCoupon,
  AvailableCouponRaw,
  ChannelAmounts,
  ChannelFee,
  ChannelListInput,
  ChannelPaymentRaw,
  CouponOrderType,
  MergePayInput,
  MyAvailableCoupon,
  MyAvailableCouponInput,
  MyAvailableCouponRaw,
  OrderPayInput,
  PayByBalanceInput,
  PaymentChannel,
  PaymentChannelList,
  PaymentChannelListRaw,
  PaymentChannelRaw,
  PaymentResult,
  PaymentResultRaw,
  PaymentResultStatus,
  PayStatus,
} from "../types/payment";

const PAY_STATUSES: readonly PayStatus[] = [
  "WAITING",
  "SUCCESS",
  "CANCELLED",
  "FAILED",
  "REFUNDED",
];

const CLIENT_TYPE_WEB = "WEB";

/** major units (BigDecimal) → integer kopecks */
function toKopecks(major: number | undefined | null): number {
  return Math.round((major ?? 0) * 100);
}

/**
 * Backend serializes dates as "yyyy-MM-dd HH:mm:ss" in GMT+8 (see
 * PaymentChannelListVo @JsonFormat). Parse to epoch ms by appending the +08:00
 * offset so the countdown is correct regardless of the viewer's timezone.
 */
function parseGmt8(s: string | undefined): number | null {
  if (!s) return null;
  const iso = `${s.replace(" ", "T")}+08:00`;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

function normalizeAmounts(raw: ChannelPaymentRaw | undefined): ChannelAmounts | null {
  if (!raw) return null;
  return {
    costFeeKopecks: toKopecks(raw.costFee?.amount),
    tradeAmountKopecks: toKopecks(raw.tradeAmount?.amount),
    actAmountKopecks: toKopecks(raw.actAmount?.amount),
    discountAmountKopecks: toKopecks(raw.discountAmount?.amount),
  };
}

/**
 * Build the per-channel handling-fee chip from the backend ExpressCalcVo +
 * the channel's strike-through originals. `isFree` ⇔ rate AND fixed both zero
 * (the only case the archived cashiers rendered the green "no fee" badge).
 */
function normalizeFee(raw: PaymentChannelRaw): ChannelFee {
  const ratePercent = raw.expressCalcVo?.channelRate ?? 0;
  const fixedKopecks = toKopecks(raw.expressCalcVo?.channelFixFee);
  return {
    ratePercent,
    fixedKopecks,
    currencyCode: raw.expressCalcVo?.currencyCode ?? "",
    currencySymbol: raw.expressCalcVo?.currencySymbol ?? "",
    isFree: ratePercent === 0 && fixedKopecks === 0,
    originalRatePercent: raw.originalRate != null && raw.originalRate > 0 ? raw.originalRate : null,
    originalFixedKopecks:
      raw.originalFeeAmount != null && raw.originalFeeAmount > 0
        ? toKopecks(raw.originalFeeAmount)
        : null,
  };
}

function normalizeChannel(raw: PaymentChannelRaw): PaymentChannel {
  return {
    id: raw.id ?? raw.payCode ?? "",
    name: raw.name ?? "",
    payCode: raw.payCode ?? "",
    icon: raw.icon ?? "",
    subIcon: raw.subIcon ?? "",
    description: raw.description ?? "",
    balanceKopecks: raw.balance != null ? toKopecks(raw.balance) : null,
    minAmountKopecks: raw.minAmount != null ? toKopecks(raw.minAmount) : null,
    maxAmountKopecks: raw.maxAmount != null ? toKopecks(raw.maxAmount) : null,
    amounts: normalizeAmounts(raw.paymentVo),
    fee: normalizeFee(raw),
    forbid: raw.forbid ?? null,
    forbidReason: raw.forbidReason ?? [],
    supportCurrencies: raw.supportCurrencies ?? null,
    required: raw.required ?? false,
    mergePayFlag: raw.mergePayFlag ?? false,
    dailyLimitFlag: raw.dailyLimitFlag ?? false,
  };
}

function normalizeChannelList(raw: PaymentChannelListRaw): PaymentChannelList {
  return {
    tradeNo: raw.tradeNo ?? "",
    orderNo: raw.orderNo ?? "",
    orderAmountKopecks: toKopecks(raw.amount?.amount),
    serviceFeeKopecks: toKopecks(raw.serviceFee?.amount),
    currencyCode: raw.amount?.currency ?? raw.currencyMoney?.currency ?? "RUB",
    currencySymbol: raw.amount?.symbol ?? raw.currencyMoney?.symbol ?? "₽",
    expireAtMs: parseGmt8(raw.expireTime),
    serverNowMs: parseGmt8(raw.nowServerTime),
    balanceKopecks: toKopecks(raw.balance),
    goldCoinKopecks: toKopecks(raw.goldCoin),
    goldCoinCnyFen: toKopecks(raw.goldCoinCNY),
    channels: (raw.paymentList ?? []).map(normalizeChannel),
  };
}

/**
 * POST /channel/list — enters the cashier; backend creates the pay order and
 * returns wallet/coin balances + available payment channels. Passing
 * `mergePayChannelCode` (balance|goldcoin) returns the slave channels usable to
 * pay the remaining tail in a combined payment.
 */
export async function queryPaymentChannelList(
  ctx: ApiFetchContext,
  authToken: string,
  input: ChannelListInput,
): Promise<PaymentChannelList> {
  const raw = await apiFetch<PaymentChannelListRaw>(ctx, "/channel/list", {
    method: "POST",
    body: {
      orderNo: input.orderNo,
      currencyCode: input.currencyCode,
      ...(input.mergePayChannelCode ? { mergePayChannelCode: input.mergePayChannelCode } : {}),
    },
    authToken,
  });
  return normalizeChannelList(raw);
}

/** POST /balance/pay — pure wallet-balance payment. */
export async function payByBalance(
  ctx: ApiFetchContext,
  authToken: string,
  input: PayByBalanceInput,
): Promise<string> {
  return apiFetch<string>(ctx, "/balance/pay", {
    method: "POST",
    body: {
      orderNo: input.orderNo,
      password: "",
      payAmount: input.payAmount,
      clientType: input.clientType ?? CLIENT_TYPE_WEB,
    },
    authToken,
  });
}

/** POST /order/pay — third-party / gold-coin payment. */
export async function payOrder(
  ctx: ApiFetchContext,
  authToken: string,
  input: OrderPayInput,
): Promise<PaymentResult> {
  return apiFetch<PaymentResult>(ctx, "/order/pay", {
    method: "POST",
    body: {
      orderNo: input.orderNo,
      channelCode: input.channelCode,
      currencyCode: input.currencyCode,
      payAmount: input.payAmount,
      clientType: input.clientType ?? CLIENT_TYPE_WEB,
      ...(input.additionalParam ? { additionalParam: input.additionalParam } : {}),
    },
    authToken,
  });
}

/** POST /order/mergePay — combined master(balance|goldcoin) + slave(3rd-party). */
export async function payOrderMerge(
  ctx: ApiFetchContext,
  authToken: string,
  input: MergePayInput,
): Promise<PaymentResult> {
  return apiFetch<PaymentResult>(ctx, "/order/mergePay", {
    method: "POST",
    body: {
      orderNo: input.orderNo,
      channelCodeMaster: input.channelCodeMaster,
      channelCodeSlave: input.channelCodeSlave,
      currencyCode: input.currencyCode,
      payAmount: input.payAmount,
      clientType: input.clientType ?? CLIENT_TYPE_WEB,
      ...(input.additionalParam ? { additionalParam: input.additionalParam } : {}),
    },
    authToken,
  });
}

function normalizeCoupon(raw: AvailableCouponRaw): AvailableCoupon {
  const reduce = raw.reduceAmount ?? raw.discountAmount;
  return {
    couponCustomerId: raw.couponCustomerId ?? "",
    name: raw.couponName ?? raw.name ?? "",
    discountKopecks: reduce != null && reduce > 0 ? toKopecks(reduce) : null,
    discountPercent: raw.discount != null && raw.discount > 0 ? raw.discount : null,
    expireTime: raw.expireTime ?? null,
  };
}

/** GET /coupon-customer/my-available-coupon-list — coupons usable for an order. */
export async function queryAvailableCoupons(
  ctx: ApiFetchContext,
  authToken: string,
  orderNo: string,
): Promise<AvailableCoupon[]> {
  const raw = await apiFetch<AvailableCouponRaw[]>(
    ctx,
    `/coupon-customer/my-available-coupon-list?orderNo=${encodeURIComponent(orderNo)}`,
    { authToken },
  );
  return (raw ?? []).map(normalizeCoupon);
}

function normalizeMyCoupon(raw: MyAvailableCouponRaw): MyAvailableCoupon {
  const target = raw.targetObject as CouponOrderType | undefined;
  const reduce = raw.discountAmount ?? raw.reduceAmount;
  return {
    couponCustomerId: raw.couponCustomerId ?? "",
    name: raw.couponName ?? raw.name ?? "",
    type: raw.type ?? "",
    targetObject: target === "ORDER" || target === "EXPRESS" ? target : null,
    discountKopecks: reduce != null && reduce > 0 ? toKopecks(reduce) : 0,
    discountPercent: raw.discount != null && raw.discount > 0 ? raw.discount : null,
    discountShowOff: raw.discountShowOFF != null ? toKopecks(raw.discountShowOFF) : 0,
    minPriceKopecks: raw.minPrice != null && raw.minPrice > 0 ? toKopecks(raw.minPrice) : 0,
    selectable: (raw.status ?? "UNUSED") === "UNUSED" && raw.isAvailable !== false,
    status: typeof raw.status === "string" ? raw.status : "UNUSED",
    noAvailableReason: raw.noAvailableReason ?? null,
    description: raw.description ?? null,
    startTime: raw.startTime ?? null,
    endTime: raw.endTime ?? null,
  };
}

/**
 * GET /coupon-customer/my-available-coupon-list?currencyCode&orderType&amount
 *
 * Pre-order coupon list for the confirm-order page (#149). Unlike the cashier
 * variant above (keyed by `orderNo`, after the order exists), this is the
 * BEFORE-order form the archived web app used: it returns every held coupon
 * plus the backend-computed `discountAmount` it would apply to `amount`. The
 * UI subtracts that authoritative number — it never computes the discount
 * itself. EXPIRED rows are dropped here so callers only see live coupons.
 */
export async function queryMyAvailableCoupons(
  ctx: ApiFetchContext,
  authToken: string,
  input: MyAvailableCouponInput,
): Promise<MyAvailableCoupon[]> {
  const qs = new URLSearchParams({
    currencyCode: input.currencyCode,
    orderType: input.orderType,
    amount: String(input.amount),
  });
  const raw = await apiFetch<MyAvailableCouponRaw[]>(
    ctx,
    `/coupon-customer/my-available-coupon-list?${qs.toString()}`,
    { authToken },
  );
  return (raw ?? []).map(normalizeMyCoupon).filter((c) => c.status !== "EXPIRED");
}

function normalizePayStatus(raw: string | undefined): PayStatus {
  const upper = (raw ?? "").toUpperCase();
  return (PAY_STATUSES as readonly string[]).includes(upper) ? (upper as PayStatus) : "WAITING";
}

/**
 * GET /payment/result/query/V2?orderNo= — polls the third-party payment result.
 *
 * Backend `queryPayResult` returns `PayResultVO { payStatus, orderNo, serverTime,
 * payGroupVO }`. After aps-boot PR #241 a not-yet-settled order returns
 * `payStatus=WAITING` instead of a 404/CustomException, so this is safe to poll
 * on an interval. `SUCCESS` is the only terminal-paid state; `FAILED`/`CANCELLED`
 * are terminal-unpaid; `WAITING` means keep polling.
 */
export async function queryPaymentResult(
  ctx: ApiFetchContext,
  authToken: string,
  orderNo: string,
): Promise<PaymentResultStatus> {
  const raw = await apiFetch<PaymentResultRaw>(
    ctx,
    `/payment/result/query/V2?orderNo=${encodeURIComponent(orderNo)}`,
    { authToken },
  );
  return {
    status: normalizePayStatus(raw?.payStatus),
    orderNo: raw?.orderNo ?? orderNo,
    serverTimeMs: typeof raw?.serverTime === "number" ? raw.serverTime : null,
    isGroup: raw?.payGroupVO != null,
  };
}
